import type { ReactNode } from 'react';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLingui } from '@lingui/react/macro';

import MenuMain from '@rover/icons/svg/menu-main.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { withOpacityRgba } from '@rover/kibble/utils';

import { PLAY_BADGE_OPACITY } from './constants';

type SortableImageTileProps = {
  id: string;
  position: number;
  children: ReactNode;
  sx?: Record<string, unknown>;
};

const DRAGGING_TILE_OPACITY = 0.4;
const DRAG_HANDLE_SIZE_PX = '32px';
const DRAG_HANDLE_GLYPH_SIZE_PX = '16px';

const HIDDEN_UNTIL_FOCUSED = {
  clip: 'rect(0 0 0 0)',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
};

const SortableImageTile = ({ id, position, children, sx }: SortableImageTileProps): JSX.Element => {
  const { t } = useLingui();
  const theme = useTheme();
  const dragHandleBackground = withOpacityRgba(theme.textColor.primary, PLAY_BADGE_OPACITY);
  const dragHandleFill = theme.textColor.primary_inverse;
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const { onKeyDown, ...pointerListeners } = listeners ?? {};
  const keyboardListeners: DraggableSyntheticListeners = onKeyDown ? { onKeyDown } : undefined;

  const handleAttributes = { ...attributes, role: 'button' as const };

  return (
    <Box
      ref={setNodeRef}
      position="relative"
      width="100%"
      data-testid="sortable-image-tile"
      {...pointerListeners}
      sx={{
        ...sx,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? DRAGGING_TILE_OPACITY : 1,
        zIndex: isDragging ? 1 : undefined,
        cursor: 'grab',
        touchAction: 'pan-y',
      }}
    >
      {children}
      <Flex
        ref={setActivatorNodeRef}
        position="absolute"
        top="2x"
        left="2x"
        zIndex="1"
        borderRadius="round"
        justifyContent="center"
        alignItems="center"
        data-testid="drag-handle"
        aria-label={t`Reorder photo ${position}`}
        {...handleAttributes}
        {...keyboardListeners}
        sx={{
          ...HIDDEN_UNTIL_FOCUSED,
          backgroundColor: dragHandleBackground,
          cursor: 'grab',
          ':focus-visible': {
            clip: 'auto',
            width: DRAG_HANDLE_SIZE_PX,
            height: DRAG_HANDLE_SIZE_PX,
            overflow: 'visible',
          },
        }}
      >
        <MenuMain
          height={DRAG_HANDLE_GLYPH_SIZE_PX}
          width={DRAG_HANDLE_GLYPH_SIZE_PX}
          fill={dragHandleFill}
        />
      </Flex>
    </Box>
  );
};

export default SortableImageTile;
